'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { Faq, ProjectSettingsModeGroupEnum } from '@/app/api';
import { useToast } from '@/app/hooks/useToast';
import { PropsWithClassname } from '@/app/types';
import { projectFaqsApiForClient } from '@/app/utils/apiForClient';

import Collapsible from '@/app/components/Collapsible/Collapsible';
import Loader from '@/app/components/Loader/Loader';
import SubprojectBanner from '@/app/components/SubprojectBanner/SubprojectBanner';
import { countryConfig } from '@/app/utils/countryConfig';

interface ProjectFAQTabProps extends PropsWithClassname {
  projectId: string;
  projectNumericId: number;
  initialData?: Faq[];
  isSubprojectBannerVisible?: boolean;
  projectType: ProjectSettingsModeGroupEnum;
}

const ProjectFAQTab = ({
  projectId,
  projectNumericId,
  initialData,
  isSubprojectBannerVisible,
  className,
  projectType,
}: ProjectFAQTabProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [faqs, setFaqs] = useState<Faq[]>(initialData ?? []);
  const { showGenericErrorToast } = useToast();

  useEffect(() => {
    const getFAQs = async () => {
      try {
        setIsLoading(true);

        const response = await projectFaqsApiForClient.getProjectFaqs({
          project: projectId,
        });

        const nextFaqs = response.faqs;

        if (!nextFaqs) {
          return null;
        }

        return setFaqs(nextFaqs);
      } catch (error) {
        showGenericErrorToast();
      } finally {
        setIsLoading(false);
      }
    };

    if (!initialData?.length) {
      getFAQs();
    }
  }, [projectId, initialData]);

  return (
    <div className={classNames(className, 'flex flex-col items-center')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          {faqs.map((faq) => {
            if (!faq.question || !faq.answer) {
              return;
            }

            return <Collapsible key={faq.id} heading={faq.question} text={faq.answer} />;
          })}

          {countryConfig.faqs[projectType].map(([question, answer]) => (
            <Collapsible key={question} heading={question} text={answer} />
          ))}
        </>
      )}
      {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
    </div>
  );
};

export default ProjectFAQTab;
